import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { Button, Placeholder } from '@stitch-fix/mode-react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import { sendMetric } from '../../../utils/sendDatadogMetric';
import { useProductDetailsPageContext } from '../../../../../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import BNC from '../../../tracking/bncTracker';
import CML from '../../../tracking/cmlTracker';
import metaValue from '../../../utils/metaValue';
import OrderType from '../../../helpers/OrderType';
import SourceType from '../../../helpers/SourceType';
import ShoppingBagTracker from '../../../../../AddToBagButton/shoppingBagTracker';
import StyleShuffleShopTracker from '../../../tracking/styleShuffleShopTracker';
import ClickTooltip from '../../../../../shared/ClickTooltip/ClickTooltip';
import {
  allowClick,
  unselectedStateMessage,
} from '../../../../../../helpers/unselectedStateHelper';
import './BuyNow.module.scss';

const BuyNow = ({ label }) => {
  const { reportSelectEvent } = useSelectEvent();
  const [state, dispatch] = useProductDetailsPageContext();
  const productSkuId = state.selectedSkuId || 0;
  const outOfStock = state.isSkuOutOfStock || false;
  const collectionName = state.collectionName || '';
  const source = state.source;
  const subSource = state.subSource || '';
  const externalProductId = state.externalProductId || '';

  const onSubmit = () => {
    sendMetric('buy_now_clicked', {
      gated: state.gated,
      selectedSkuIdSameAsOnPageLoad:
        state.selectedSkuId === state.recommendedSkuId,
      selectedColorIsSameAsOnPageLoad:
        state.selectedUniqueColorId === state.selectedUniqueColorIdOnPageLoad,
      selectedSizeIsSameAsOnPageLoad:
        state.selectedSizeLabel === state.selectedSizeLabelOnPageLoad,
      selectedInseamIsSameAsOnPageLoad:
        state.selectedInseamLabel === state.selectedInseamLabelOnPageLoad,
    });

    reportSelectEvent({
      schema: 'sku_select',
      name: 'buy_now',
      action_name: 'buy_now',
      sku_id: productSkuId,
    });

    const revenueInCents = state.sku?.price?.value || 0;

    // NOTE: all attributes are optionally-chained as we never want problems with
    //       this event to cause issues with the page or purchases.
    fireEvent({
      eventName: 'buy_now_button_click',
      eventAttributes: {
        revenue: (revenueInCents / 100).toFixed(2),
        business_line: state.clientBusinessLine,
        sku_id: state.sku?.id,
        externalProductId: state.externalProductId,
        pdp_type: 'gated',
      },
    });

    sendMetric('pdp_buy_now_submit', {
      business_line: state.clientBusinessLine,
      alternate_size_selected: false,
    });

    const sharedContext = {
      skuId: productSkuId,
      externalProductId,
      source,
      subSource,
    };

    if (source === SourceType.SYL) {
      CML.trackBuyNowEvent({
        outfitId: state.outfitId,
        collection: collectionName,
        ...sharedContext,
      });
    } else if (source === SourceType.SHOPPING_BAG) {
      ShoppingBagTracker.trackBuyNowEvent({
        keptSkuId: productSkuId,
        ...sharedContext,
      });
    } else if (source === SourceType.CATEGORIES) {
      StyleShuffleShopTracker.trackBuyNowEvent({
        collection: collectionName,
        ...sharedContext,
      });
    } else {
      BNC.trackBuyNowEvent({
        keptSkuId: productSkuId,
        ...sharedContext,
      });
    }

    return true;
  };

  const orderType =
    source === SourceType.CATEGORIES ? OrderType.CAT : OrderType.BN;

  const shouldDisable = outOfStock || productSkuId === 0;

  const buttonText = label || (outOfStock ? 'Out of stock' : 'Buy now');

  if (state.loading || !state.shopProduct) {
    return <Placeholder height={4} />;
  }

  return allowClick(state) ? (
    <div styleName="container">
      <form action="/my-items/orders/review" method="post" onSubmit={onSubmit}>
        <input
          type="hidden"
          name="authenticity_token"
          value={metaValue('csrf-token')}
        />
        <input
          type="hidden"
          name="order[order_id]"
          value={uuidv4()}
          data-testid="order_id"
        />
        <input
          type="hidden"
          name="order[idempotency_key]"
          value={uuidv4()}
          data-testid="idempotency_key"
        />
        <input
          type="hidden"
          name="order[order_type]"
          value={orderType}
          data-testid="order_type"
        />
        <input
          type="hidden"
          name="order[skus][][id]"
          value={productSkuId}
          data-testid="sku_id"
        />
        <input type="hidden" name="order[skus][][source]" value={source} />
        <input
          type="hidden"
          name="order[skus][][collection]"
          value={collectionName}
        />
        <input
          type="hidden"
          name="order[skus][][metadata][outfit_id]"
          value={state.outfitId}
        />
        <input
          type="hidden"
          name="order[skus][][metadata][kept_sku_id]"
          value={productSkuId}
        />
        <input
          type="hidden"
          name="order[skus][][metadata][sub_source]"
          value={subSource || ''}
        />
        <input type="hidden" name="order[skus][][qty]" value={1} />
        <Button
          width="fill"
          type="submit"
          name="commit"
          id="buy-now--btn"
          disabled={shouldDisable}
          // TODO: needed to override min-width setting in button for small
          // screen use case.
          // eslint-disable-next-line @stitch-fix/no-dangerous-mode-react-styling-props
          dangerouslySetStyleName="button"
          variant="filled-staple"
          data-testid="buy_now_button"
        >
          {buttonText}
        </Button>
      </form>
    </div>
  ) : (
    <ClickTooltip
      text={unselectedStateMessage(state)}
      show={!allowClick(state) && state.ctaTooltipDisplay?.showBuyNow === true}
      arrowDirection="down"
    >
      <Button
        width="fill"
        variant="filled-staple"
        aria-label="warning"
        disabled={state.isSkuOutOfStock}
        onClick={() => {
          dispatch({
            type: 'DISABLED_BUY_NOW_CLICKED',
          });
        }}
        data-testid="buy-now-button-blocker"
      >
        {state.isSkuOutOfStock ? 'Out of stock' : 'Buy now'}
      </Button>
    </ClickTooltip>
  );
};

BuyNow.propTypes = {
  label: PropTypes.string,
};

BuyNow.defaultProps = {
  label: '',
};

export default BuyNow;
